import { notification } from "antd";
import { get, isEmpty } from "lodash";
import omitDeep from "omit-deep-lodash";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import Loading from "shared/components/Spin";
import { useMutation, useQuery } from "shared/hooks/useApi";
import { NAME_SPACES } from "shared/locales/constants";
import { UPDATE_PRICER } from "utils/api/graphql/mutations/pricers";
import { PRICER, PRICERS } from "utils/api/graphql/queries/pricers";
import { PRICER_STRUCTURES, PRICER_TYPES, SCREENS } from "utils/constants";
import IsoleDuoFamilleView from "../../Create/Health/IsoleDuoFamilleView";
import View from "./View";

const Detail = () => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.ADMIN.PRICER);
  const ERRORS = t("ERRORS", { returnObjects: true });
  const navigate = useNavigate();
  const cancel = () => navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);
  const { id } = useParams();

  const [updatePricer] = useMutation(UPDATE_PRICER, {
    refetchQueries: [
      {
        query: PRICERS,
        awaitRefetchQueries: true,
      },
    ],
    onCompleted: () => {
      navigate(SCREENS.PRIVATE.ADMIN.PRICERS.LIST.path);
    },
    onError: (error) => {
      notification.error({ message: error.message || ERRORS.DATE_CONFLICT });
    },
  });

  const { data, loading, error } = useQuery(PRICER, {
    variables: { where: { id } },
  });

  const onSubmit = ({ prices, isZipCode, dates, ...values }) => {
    const [startDate, endDate] = dates;
    return updatePricer({
      variables: {
        where: { id },
        data: {
          ...values,
          type: PRICER_TYPES.DB,
          startDate: startDate?.startOf("day").toISOString() || null,
          endDate: endDate?.endOf("day").toISOString() || null,
          prices: prices.map(
            ({
              costs,
              departments,
              socialRegime,
              commission,
              beneficiaryType,
              zipCodes,
            }) => ({
              costs: JSON.parse(costs),
              socialRegime,
              beneficiaryType,
              commission,
              departments: isEmpty(departments) ? undefined : departments,
              zipCodes: isEmpty(zipCodes) ? undefined : zipCodes,
            }),
          ),
        },
      },
    });
  };

  const onSubmitStructure = ({ amounts, dates, name, insurancePlan }) => {
    const [startDate, endDate] = dates;
    return updatePricer({
      variables: {
        where: { id },
        data: {
          name,
          insurancePlan,
          type: PRICER_TYPES.DB,
          startDate: startDate?.startOf("day").toISOString() || null,
          endDate: endDate?.endOf("day").toISOString() || null,
          prices: {
            structure: PRICER_STRUCTURES.ISOLATED_DUO_FAMILY,
            amounts: {
              single: Number(amounts.single),
              duo: Number(amounts.duo),
              family: Number(amounts.family),
            },
          },
        },
      },
    });
  };

  if (loading || error) return <Loading />;

  const pricer = omitDeep(data.pricer, "__typename");
  const isStructure =
    get(pricer, "prices.structure") === PRICER_STRUCTURES.ISOLATED_DUO_FAMILY;

  if (isStructure) {
    return (
      <IsoleDuoFamilleView
        onSubmit={onSubmitStructure}
        data={pricer}
        cancel={cancel}
        locked
      />
    );
  }

  return <View onSubmit={onSubmit} data={pricer} cancel={cancel} />;
};

export default Detail;
